import * as React from 'react';
import {useDispatch} from 'react-redux';

import type {Assignable} from 'nutshell-core/assignables/assignables-types';
import {
    replaceSavedFilter,
    requestResaveFilter,
    updateResaveFilter,
    failResaveFilter,
} from 'nutshell-core/saved-filters/saved-filters-actions';
import type {Team} from 'nutshell-core/teams/teams-types';
import type {SavedFilter} from 'nutshell-core/types';
import type {User} from 'nutshell-core/users/users-types';

import {Avatar} from 'shells/avatar/avatar';
import {colors} from 'shells/colors/colors';
import {GoalAudienceIcon} from 'shells/icon/svg/goal-audience-icon';

import {addNotification} from '../../../notifications/index';

import {useUpdateFilterShare} from '../../entity-list/graphql/hooks';
import type {Option} from '../../scheduler/assignment/users-teams-selection-modal';

import type {ShareType} from '../share-selection';

import {FilterShareModal} from './filter-share-modal';

type Props = {
    title: string;
    savedFilter: SavedFilter<any>;
    isOpen: boolean;
    onClose: () => any;
    shareType: ShareType;
    assignables: Assignable[];
};

export function FilterShareModalWrapper(props: Props) {
    const {savedFilter, assignables, ...restProps} = props;

    const {updateFilterShare, isLoading: isUpdating} = useUpdateFilterShare();
    const dispatch = useDispatch();

    // @ts-expect-error
    const {selectedOptions, remainingOptions} = getOptions(savedFilter, assignables);

    const handleSave = (isShared: boolean, sharedOptions: Option[]) => {
        const sharedUserIds = sharedOptions.map((option) => option.value);
        try {
            updateFilterShare({
                id: savedFilter.id,
                isShared,
                sharedWithIds: sharedUserIds,
            }).then(() => {
                // add success toast state
                addNotification('List sharing updated', {
                    icon: 'check',
                    body: getSaveToastText(isShared, props.savedFilter.name, sharedOptions),
                });
                // update redux to keep the filter in sync
                savedFilter.links.usersAndTeams = sharedUserIds;
                savedFilter.isShared = isShared;
                dispatch(requestResaveFilter());
                // @ts-expect-error
                dispatch(replaceSavedFilter(savedFilter));
                dispatch(updateResaveFilter());
            });
        } catch (error: any) {
            dispatch(failResaveFilter());
            addNotification('Something went wrong', {
                icon: 'frowny',
                body: 'We encountered a problem while sharing.',
            });
            throw new Error(error);
        }
    };

    return (
        <FilterShareModal
            {...restProps}
            isSaving={isUpdating}
            shareType={props.shareType}
            onSave={handleSave}
            selectedOptions={selectedOptions}
            remainingOptions={remainingOptions}
            listName={props.savedFilter.name}
        />
    );
}

function getOptions(
    savedFilter: SavedFilter<any>,
    usersAndTeams: User | Team[]
): {
    selectedOptions: Option[];
    remainingOptions: Option[];
} {
    // @ts-expect-error
    const [selectedUsersAndTeams, remainingUsersAndTeams] = usersAndTeams.reduce(
        // @ts-expect-error
        ([selected, remaining]: [any, any], userOrTeam) => {
            // Don't add the owner as a sharing option
            if (userOrTeam.id === savedFilter.links.user) {
                return [selected, remaining];
            }

            if (
                savedFilter.links.usersAndTeams &&
                savedFilter.links.usersAndTeams.includes(userOrTeam.id)
            ) {
                return [[...selected, userOrTeam], remaining];
            }

            return [selected, [...remaining, userOrTeam]];
        },
        [[], []]
    );

    return {
        selectedOptions: convertToOptionsObject(selectedUsersAndTeams),
        remainingOptions: convertToOptionsObject(remainingUsersAndTeams),
    };
}

function convertToOptionsObject(models: User | Team[]): Option[] {
    // @ts-expect-error
    return models.map((model) => ({
        value: model.id,
        label: model.name,
        sublabel: model.type === 'users' ? model.emails[0] : `${model.numUsers} members`,
        iconNode:
            model.type === 'teams' ? (
                // @ts-expect-error
                <GoalAudienceIcon size={23} color={colors.orangeDk} />
            ) : (
                <Avatar
                    size={23}
                    avatarUrl={model.avatarUrl}
                    initials={model.initials}
                    circular={true}
                    type='users'
                />
            ),
        metaData: model.type === 'users' ? 'user' : 'team',
    }));
}

function getSaveToastText(isShared: boolean, listName: string, sharedWith: Option[]) {
    if (!isShared) {
        return `${listName} is now set to Private.`;
    }

    if (sharedWith.length === 0) {
        return `${listName} is now visible to 'Everyone' in your organization.`;
    }

    if (sharedWith.length === 1) {
        // @ts-expect-error
        return `${listName} is now visible to ${sharedWith[0].label}`;
    } else if (sharedWith.length === 2) {
        // @ts-expect-error
        return `${listName} is now visible to ${sharedWith[0].label} and ${sharedWith[1].label}`;
    } else {
        // @ts-expect-error
        return `${listName} is now visible to ${sharedWith[0].label} and ${
            sharedWith.length - 1
        } more`;
    }
}
